import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  CheckCircle2,
  Clock,
  Eye,
  ReceiptText,
  Search,
  XCircle,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import apiClient from '@/api/client'
import { useCurrency } from '@/contexts/CurrencyContext'
import { formatDateHuman, formatTimeHuman, toIsoDate, cn } from '@/lib/utils'
import { CreditNoteView } from '@/components/admin/CreditNoteView'
import type { RefundLogRow } from '@/types'

type StatusFilter = 'all' | 'synced' | 'pending' | 'failed'

const STATUS_FILTERS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'synced', label: 'Synced' },
  { id: 'pending', label: 'Pending' },
  { id: 'failed', label: 'Failed' },
]

interface Props {
  /** Calendar day to load refunds for (from Refund Logs drill-in). */
  day: Date
}

/**
 * RefundsBrowserTab — per-day refund list used inside the Refund Logs drawer.
 * Mirrors OrdersBrowserTab in spirit but narrowed to credit-note rows. Each
 * row opens the existing CreditNoteView modal, which renders a printable
 * refund/credit-note receipt.
 */
export function RefundsBrowserTab({ day }: Props) {
  const { formatCurrency } = useCurrency()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [viewNoteId, setViewNoteId] = useState<string | null>(null)
  const [viewOrderNumber, setViewOrderNumber] = useState<string | undefined>(undefined)

  const dayISO = toIsoDate(day)

  const refundsQuery = useQuery({
    queryKey: ['reports-v2', 'refunds-by-day', dayISO, search.trim(), statusFilter],
    queryFn: () =>
      apiClient.getRefundsByDay(dayISO, {
        search: search.trim() || undefined,
        status: statusFilter,
      }),
    staleTime: 15_000,
  })

  const refunds = refundsQuery.data?.data ?? []

  const totals = useMemo(() => {
    return refunds.reduce(
      (acc, r) => ({
        count: acc.count + 1,
        gross: acc.gross + r.total_amount,
        failed: acc.failed + (r.status === 'failed' ? 1 : 0),
      }),
      { count: 0, gross: 0, failed: 0 },
    )
  }, [refunds])

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search order # or reason"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="flex items-center gap-1 rounded-md bg-muted p-1">
            {STATUS_FILTERS.map((f) => (
              <Button
                key={f.id}
                type="button"
                size="sm"
                variant={statusFilter === f.id ? 'default' : 'ghost'}
                className="h-7 px-3 text-xs"
                onClick={() => setStatusFilter(f.id)}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-2">
          <CardTitle className="text-base">
            Refunds on {formatDateHuman(day)} ({refunds.length})
          </CardTitle>
          <div className="text-xs text-muted-foreground tabular-nums flex items-center gap-3 flex-wrap">
            <span>
              Total refunded:{' '}
              <span className="font-semibold text-foreground">{formatCurrency(totals.gross)}</span>
            </span>
            {totals.failed > 0 && (
              <span className="text-red-700 dark:text-red-400">
                Failed: <span className="font-semibold">{totals.failed}</span>
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {refundsQuery.isLoading ? (
            <div className="p-6 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : refundsQuery.isError ? (
            <div className="p-10 text-center text-sm text-destructive">
              Failed to load refunds: {String((refundsQuery.error as Error)?.message ?? 'unknown')}
            </div>
          ) : refunds.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No refunds match these filters for {formatDateHuman(day)}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2.5">Order #</th>
                    <th className="text-left px-4 py-2.5">Time</th>
                    <th className="text-right px-4 py-2.5">Refund total</th>
                    <th className="text-left px-4 py-2.5">Reason</th>
                    <th className="text-left px-4 py-2.5">Issued by</th>
                    <th className="text-left px-4 py-2.5">FBR IRN</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    <th className="text-right px-4 py-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {refunds.map((row) => (
                    <RefundRow
                      key={row.id}
                      row={row}
                      formatCurrency={formatCurrency}
                      onView={() => {
                        setViewNoteId(row.id)
                        setViewOrderNumber(row.order_number)
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {viewNoteId && (
        <CreditNoteView
          noteId={viewNoteId}
          orderNumber={viewOrderNumber}
          onClose={() => {
            setViewNoteId(null)
            setViewOrderNumber(undefined)
          }}
        />
      )}
    </div>
  )
}

function RefundRow({
  row,
  formatCurrency,
  onView,
}: {
  row: RefundLogRow
  formatCurrency: (n: number) => string
  onView: () => void
}) {
  const timePart = formatTimeHuman(row.created_at)
  return (
    <tr className="hover:bg-muted/20">
      <td className="px-4 py-3 font-medium tabular-nums">{row.order_number}</td>
      <td className="px-4 py-3 text-muted-foreground tabular-nums">{timePart}</td>
      <td className="text-right tabular-nums px-4 py-3 font-semibold text-red-700 dark:text-red-300">
        −{formatCurrency(row.total_amount)}
      </td>
      <td className="px-4 py-3 text-muted-foreground italic max-w-[260px] truncate">
        {row.reason ?? '—'}
      </td>
      <td className="px-4 py-3 text-muted-foreground">{row.issued_by_name ?? '—'}</td>
      <td className="px-4 py-3 text-muted-foreground font-mono text-[11px]">
        {row.fiscal_irn ?? '—'}
      </td>
      <td className="px-4 py-3">
        <RefundStatusBadge status={row.status} />
      </td>
      <td className="px-4 py-3 text-right">
        <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={onView}>
          <Eye className="w-3.5 h-3.5" /> View
        </Button>
      </td>
    </tr>
  )
}

function RefundStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'synced':
      return (
        <Badge
          variant="outline"
          className={cn(
            'text-[10px] gap-1 border-emerald-200 bg-emerald-50 text-emerald-800',
            'dark:border-emerald-900/40 dark:bg-emerald-950 dark:text-emerald-200',
          )}
        >
          <CheckCircle2 className="w-3 h-3" /> Synced
        </Badge>
      )
    case 'pending':
      return (
        <Badge
          variant="outline"
          className={cn(
            'text-[10px] gap-1 border-amber-200 bg-amber-50 text-amber-800',
            'dark:border-amber-900/40 dark:bg-amber-950 dark:text-amber-200',
          )}
        >
          <Clock className="w-3 h-3" /> Pending
        </Badge>
      )
    case 'failed':
      return (
        <Badge
          variant="outline"
          className={cn(
            'text-[10px] gap-1 border-red-200 bg-red-50 text-red-800',
            'dark:border-red-900/40 dark:bg-red-950 dark:text-red-200',
          )}
        >
          <XCircle className="w-3 h-3" /> Failed
        </Badge>
      )
    case 'draft':
      return (
        <Badge
          variant="outline"
          className="text-[10px] gap-1 border-muted bg-muted text-muted-foreground"
        >
          <ReceiptText className="w-3 h-3" /> Draft
        </Badge>
      )
    default:
      return (
        <Badge
          variant="outline"
          className="text-[10px] gap-1 border-muted bg-muted text-muted-foreground"
        >
          {status}
        </Badge>
      )
  }
}
